import { useState, useEffect } from 'react'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { Users, Building2, Plane, Shield, RefreshCw, Loader2, Search } from 'lucide-react'
import { lawyerRoute } from './index'
import { kontakteAPI, type KontaktListItem, type KontaktTag } from '@/utils/api'
import { CreateKontaktModal } from '@/components/lawyer/kontakt-detail'

type TabFilter = 'all' | 'person' | 'company' | 'airline' | 'rsv'

function KontaktePage() {
  const navigate = useNavigate()
  const [kontakte, setKontakte] = useState<KontaktListItem[]>([])
  const [tags, setTags] = useState<KontaktTag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [activeTab, setActiveTab] = useState<TabFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const handleRowClick = (kontaktId: number) => {
    navigate({ to: '/lawyer/kontakte/$kontaktId', params: { kontaktId: String(kontaktId) } })
  }

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }
    setError(null)

    try {
      // Build filter params based on active tab
      let params: { tag?: string; type?: 'person' | 'company'; search?: string } = {}
      if (activeTab === 'person') params.type = 'person'
      else if (activeTab === 'company') params.type = 'company'
      else if (activeTab === 'airline') params.tag = 'airline'
      else if (activeTab === 'rsv') params.tag = 'rsv'

      if (searchQuery) params.search = searchQuery

      const [kontakteData, tagsData] = await Promise.all([
        kontakteAPI.getKontakte(params),
        kontakteAPI.getTags(),
      ])
      setKontakte(kontakteData)
      setTags(tagsData)
    } catch (err) {
      console.error('Failed to fetch kontakte:', err)
      setError('Kontakte konnten nicht geladen werden.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [activeTab])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    fetchData()
  }

  const tabConfig = [
    { id: 'all' as TabFilter, label: 'Alle', icon: Users },
    { id: 'person' as TabFilter, label: 'Personen', icon: Users },
    { id: 'company' as TabFilter, label: 'Firmen', icon: Building2 },
    { id: 'airline' as TabFilter, label: 'Airlines', icon: Plane },
    { id: 'rsv' as TabFilter, label: 'RSVs', icon: Shield },
  ]

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-slate-400" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Kontakte</h1>
            <p className="text-slate-500">Mandanten, Beklagte und Kontakte verwalten</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-50"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Aktualisieren
          </button>
          <CreateKontaktModal tags={tags} onCreated={() => fetchData(true)} />
        </div>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Suche nach Name, E-Mail, Telefon, IATA-Code..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:border-slate-400 focus:ring-1 focus:ring-slate-400 outline-none transition-colors"
          />
        </div>
      </form>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-slate-200">
        {tabConfig.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-64 bg-white rounded-xl border border-slate-200 shadow-sm">
          <Loader2 className="h-8 w-8 text-slate-400 animate-spin mb-4" />
          <p className="text-slate-500">Kontakte werden geladen...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-64 bg-white rounded-xl border border-red-200 shadow-sm">
          <Users className="h-16 w-16 text-red-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Fehler</h3>
          <p className="text-slate-500 text-center max-w-md mb-4">{error}</p>
          <button
            onClick={() => fetchData()}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors"
          >
            Erneut versuchen
          </button>
        </div>
      ) : kontakte.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 bg-white rounded-xl border border-slate-200 shadow-sm">
          <Users className="h-16 w-16 text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Keine Kontakte</h3>
          <p className="text-slate-500 text-center max-w-md">
            Es wurden keine Kontakte gefunden.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Typ</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Tags</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">E-Mail</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Telefon</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Akten</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {kontakte.map((kontakt) => (
                <tr
                  key={kontakt.id}
                  onClick={() => handleRowClick(kontakt.id)}
                  className="hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        kontakt.kontakt_type === 'person' ? 'bg-blue-100' : 'bg-purple-100'
                      }`}>
                        {kontakt.kontakt_type === 'person' ? (
                          <Users className={`h-4 w-4 text-blue-600`} />
                        ) : (
                          <Building2 className={`h-4 w-4 text-purple-600`} />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{kontakt.display_name}</p>
                        {kontakt.iata_code && (
                          <p className="text-sm text-slate-500 font-mono">{kontakt.iata_code}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {kontakt.kontakt_type === 'person' ? 'Person' : 'Firma'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {kontakt.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700"
                        >
                          {tag.display_name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {kontakt.primary_email || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {kontakt.primary_phone || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-sm font-medium rounded-full bg-slate-100 text-slate-700">
                      {kontakt.akten_count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export const lawyerKontakteRoute = createRoute({
  getParentRoute: () => lawyerRoute,
  path: '/kontakte',
  component: KontaktePage,
})
